import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ShoppingCart, User, Package, CreditCard, Check, RefreshCw, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminPointsCalculator from '@/components/sales/AdminPointsCalculator';
import AdminServiceAboCalculator from '@/components/sales/AdminServiceAboCalculator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SalesPage = () => {
  const [step, setStep] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [productType, setProductType] = useState('');
  const [pointPackages, setPointPackages] = useState([]);
  const [subscriptionTemplates, setSubscriptionTemplates] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountCreating, setDiscountCreating] = useState(false);
  
  // NEU: Rabatt-State
  const [discountType, setDiscountType] = useState('fixed'); // 'fixed' oder 'percent'
  const [discountValue, setDiscountValue] = useState('');

  // NEU 06/2026: Einmalige Rechnung
  const [oneTimeForm, setOneTimeForm] = useState({
    description: '',
    amount: '',
  });
  const [oneTimeResult, setOneTimeResult] = useState(null);  // {invoice_number, payrexx_payment_link, qr_reference}

  // Recurring subscription fields (wie im Kundenprofil)
  // WICHTIG: MUSS vor calculateDiscount() deklariert sein (TDZ-Crash im Prod-Build)
  const [recurringFormData, setRecurringFormData] = useState({
    name: '',
    description: '',
    points_per_month: 100,
    price_per_month: 200,
    billing_cycle: 'monthly',
    payment_method: 'kreditkarte', // Immer Kreditkarte für wiederkehrende Abos
    // 06/2026 Phase 3 — Wiederkehrende Abrechnungs-Methode:
    //   "invoice" = QR-Rechnung an Kunde (Default)
    //   "card"    = Auto-Charge via Payrexx
    auto_billing_method: 'invoice',
    allowed_service_ids: [],
    // Flexible Initialzahlung (optional)
    initial_price: null,
    initial_points: null,
    // 06/2026 — „Erste Rechnung geht raus am" (Optional ISO date)
    delayed_start_date: null,
    // 06/2026 — Konfigurierbarer Abbuchungstag (1-28). Leer = globaler Default
    billing_day: null,
    // Auto-Reload Feature
    renewal_mode: 'monthly_25',
    low_balance_threshold: 2,
  });
  
  // Berechne den Rabatt und Endpreis
  const calculateDiscount = () => {
    const originalPrice = productType === 'recurring' 
      ? parseFloat(recurringFormData.price_per_month) || 0
      : productType === 'one_time'
        ? parseFloat(oneTimeForm.amount) || 0
        : parseFloat(selectedProduct?.price) || 0;
    
    if (!discountValue || discountValue === '' || discountValue === '0') {
      return { discount: 0, finalPrice: originalPrice };
    }
    
    const discountNum = parseFloat(discountValue) || 0;
    let discount = 0;
    
    if (discountType === 'percent') {
      discount = originalPrice * (discountNum / 100);
    } else {
      discount = discountNum;
    }
    
    const finalPrice = Math.max(0, originalPrice - discount);
    return { discount, finalPrice, originalPrice };
  };

  // 07/2026: CHF 0.00 im Checkout (100% Rabatt / Gratis) → nur Barzahlung
  const checkoutFinal = calculateDiscount().finalPrice;
  const zeroCheckout = step === 4 && productType !== 'recurring' && checkoutFinal === 0 && !!selectedProduct;
  const calculatorHasDiscount = !!(selectedProduct?.sale_note || '').includes('Rabatt');
  const noteRequired = zeroCheckout || calculatorHasDiscount || (!!discountValue && parseFloat(discountValue) > 0);

  useEffect(() => {
    if (zeroCheckout && paymentMethod !== 'bar') setPaymentMethod('bar');
  }, [zeroCheckout, paymentMethod]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedCustomer?.id) {
      loadDiscountCodes(selectedCustomer.id);
    } else {
      setDiscountCodes([]);
    }
  }, [selectedCustomer]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [customersRes, packagesRes, subsRes, servicesRes] = await Promise.all([
        axios.get(`${API}/customers/with-users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/point-packages`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/subscriptions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/services`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setCustomers(customersRes.data);
      setFilteredCustomers(customersRes.data);
      setPointPackages(packagesRes.data.filter(p => p.is_active));
      setSubscriptionTemplates(subsRes.data.filter(s => !s.customer_id));
      setServices(servicesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Fehler beim Laden der Daten');
    }
  };

  const loadDiscountCodes = async (customerId) => {
    setDiscountLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/discount-codes?customer_id=${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiscountCodes(response.data || []);
    } catch (error) {
      console.error('Error loading discount codes:', error);
      setDiscountCodes([]);
    } finally {
      setDiscountLoading(false);
    }
  };

  const handleCreateDiscountCode = async () => {
    if (!selectedCustomer?.id) {
      toast.error('Bitte zuerst einen Kunden auswählen');
      return;
    }
    setDiscountCreating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/discount-codes`,
        {
          customer_id: selectedCustomer.id,
          discount_percent: 10,
          valid_days: 30,
          source: 're_engagement_60d'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Rabattcode erstellt: ${response.data.code}`);
      loadDiscountCodes(selectedCustomer.id);
    } catch (error) {
      console.error('Error creating discount code:', error);
      toast.error('Rabattcode konnte nicht erstellt werden');
    } finally {
      setDiscountCreating(false);
    }
  };

  const handleCustomerSearch = (searchTerm) => {
    setCustomerSearch(searchTerm);
    if (!searchTerm.trim()) {
      setFilteredCustomers(customers);
      return;
    }
    
    const filtered = customers.filter(c => {
      const fullName = `${c.first_name} ${c.last_name}`.toLowerCase();
      const email = c.email?.toLowerCase() || '';
      const search = searchTerm.toLowerCase();
      return fullName.includes(search) || email.includes(search);
    });
    setFilteredCustomers(filtered);
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    // Set default payment method from customer profile
    // Map customer billing_mode to our simplified options
    let defaultPayment = 'rechnung_combo';
    if (customer.billing_mode === 'bar') {
      defaultPayment = 'bar';
    } else if (customer.billing_mode === 'card' || customer.billing_mode === 'kreditkarte') {
      defaultPayment = 'card';
    }
    setPaymentMethod(defaultPayment);
    setCustomerSearch('');
    setStep(2);
  };

  const handleProductTypeSelect = (type) => {
    setProductType(type);
    if (type === 'recurring') {
      // Recurring subscriptions - show template selection first
      setStep(3);
    } else {
      setStep(3);
    }
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setStep(4);
  };

  const handlePurchase = async () => {
    if (!selectedCustomer) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Berechne Rabatt
      const { discount, finalPrice } = calculateDiscount();
      const hasDiscount = discountValue && parseFloat(discountValue) > 0;

      // 07/2026: Rabatt/0-CHF-Regeln — Notiz Pflicht, bei CHF 0 nur Bar
      const effAmount = productType === 'points' ? (hasDiscount ? finalPrice : parseFloat(selectedProduct?.price) || 0) : finalPrice;
      if ((hasDiscount || calculatorHasDiscount || effAmount === 0) && !notes.trim()) {
        toast.error('Bitte eine Notiz mit Begründung für den Rabatt / CHF 0.00 angeben');
        setLoading(false);
        return;
      }
      if (productType !== 'recurring' && effAmount === 0 && paymentMethod !== 'bar') {
        toast.error('Bei CHF 0.00 ist nur Barzahlung möglich');
        setLoading(false);
        return;
      }
      const discountNote = hasDiscount 
        ? `Rabatt: ${discountType === 'percent' ? `${discountValue}%` : `CHF ${discountValue}`} (-CHF ${discount.toFixed(2)})` 
        : '';
      const fullNotes = [notes, discountNote].filter(Boolean).join(' | ');
      
      if (productType === 'service_abo') {
        if (!selectedProduct?.service_id) { setLoading(false); return; }
        const { data } = await axios.post(
          `${API}/customers/${selectedCustomer.id}/purchase-service-abo`,
          {
            service_id: selectedProduct.service_id,
            quantity: selectedProduct.quantity,
            payment_method: (paymentMethod === 'rechnung_combo' ? 'rechnung' : paymentMethod) || 'rechnung',
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`✅ ${selectedProduct.name} verkauft — CHF ${(data?.total_amount ?? selectedProduct.price)?.toFixed ? (data?.total_amount ?? selectedProduct.price).toFixed(2) : selectedProduct.price}`);
      } else if (productType === 'points') {        if (!selectedProduct) return;
        // Immer Standard-Werte vom Paket verwenden
        const pointsToAdd = selectedProduct.points_amount;
        const originalAmount = selectedProduct.price;
        // WICHTIG: Original-(Brutto-)Preis senden. Das Backend zieht den Rabatt
        // (discount_type/value) selbst EINMAL ab und speichert Originalpreis +
        // Rabatt auf der Rechnung. Früher wurde hier bereits finalPrice gesendet
        // → Backend zog den Rabatt ein zweites Mal ab (2775-375-375=2025 statt 2400).
        const amount = originalAmount;
        const customSaleNotes = [selectedProduct.sale_note, fullNotes].filter(Boolean).join(' | ');
        
        await axios.post(
          `${API}/customers/${selectedCustomer.id}/add-points`,
          {
            points: parseInt(pointsToAdd),
            amount_chf: parseFloat(amount),
            payment_method: paymentMethod,
            package_id: selectedProduct.custom_points ? null : selectedProduct.id,
            bonus_points: selectedProduct.bonus_points || 0,
            notes: selectedProduct.custom_points ? customSaleNotes : fullNotes,
            discount_type: hasDiscount ? discountType : null,
            discount_value: hasDiscount ? parseFloat(discountValue) : null,
            discount_amount: hasDiscount ? discount : null
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast.success(`✅ ${pointsToAdd} Punkte erfolgreich verkauft!${hasDiscount ? ` (mit Rabatt)` : ''}`);
      } else if (productType === 'subscription') {
        if (!selectedProduct) return;
        await axios.post(
          `${API}/customers/${selectedCustomer.id}/purchase-subscription`,
          {
            subscription_template_id: selectedProduct.id,
            payment_method: paymentMethod,
            notes: fullNotes,
            discount_type: hasDiscount ? discountType : null,
            discount_value: hasDiscount ? parseFloat(discountValue) : null,
            discount_amount: hasDiscount ? discount : null,
            final_price: hasDiscount ? finalPrice : null
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast.success(`✅ ${selectedProduct.name} erfolgreich verkauft!${hasDiscount ? ` (mit Rabatt)` : ''}`);
      } else if (productType === 'one_time') {
        // 06/2026: Einmalige Rechnung mit fixem Betrag (QR + Payrex-Link)
        const finalAmount = hasDiscount ? finalPrice : parseFloat(oneTimeForm.amount);
        if (!oneTimeForm.description || !finalAmount || finalAmount <= 0) {
          toast.error('Beschreibung und Betrag sind erforderlich');
          setLoading(false);
          return;
        }
        const { data } = await axios.post(
          `${API}/sales/one-time-invoice`,
          {
            customer_id: selectedCustomer.id,
            description: oneTimeForm.description,
            amount: finalAmount,
            notes: fullNotes,
            payment_method: paymentMethod || 'rechnung_combo',
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setOneTimeResult(data);
        toast.success(`✅ Rechnung ${data.invoice_number} erstellt — CHF ${data.total_amount}`);
        // Form NICHT sofort resetten — der Admin soll Payment-Link & QR-Ref sehen können
        setLoading(false);
        return;
      } else if (productType === 'recurring') {
        // Check if we're using a template or custom subscription
        if (selectedProduct) {
          // Use existing template - similar to subscription purchase
          await axios.post(
            `${API}/customers/${selectedCustomer.id}/purchase-subscription`,
            {
              subscription_template_id: selectedProduct.id,
              payment_method: 'card', // Recurring always uses card
              notes: fullNotes,
              is_recurring: true, // Mark as recurring
              auto_billing_method: recurringFormData.auto_billing_method, // 06/2026 Phase 3
              discount_type: hasDiscount ? discountType : null,
              discount_value: hasDiscount ? parseFloat(discountValue) : null,
              discount_amount: hasDiscount ? discount : null,
              final_price: hasDiscount ? finalPrice : null
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          toast.success(`✅ ${selectedProduct.name} (Wiederkehrend) erfolgreich verkauft!${hasDiscount ? ` (mit Rabatt)` : ''}`);
        } else {
          // Custom recurring subscription
          const recurringPrice = hasDiscount ? finalPrice : parseFloat(recurringFormData.price_per_month);
          
          await axios.post(
            `${API}/recurring-subscriptions`,
            {
              customer_id: selectedCustomer.id,
              name: recurringFormData.name,
              description: recurringFormData.description,
              points_per_month: parseInt(recurringFormData.points_per_month),
              price_per_month: recurringPrice,
              billing_cycle: recurringFormData.billing_cycle,
              payment_method: recurringFormData.payment_method,
              auto_billing_method: recurringFormData.auto_billing_method, // 06/2026 Phase 3
              allowed_service_ids: recurringFormData.allowed_service_ids,
              initial_price: recurringFormData.initial_price,
              initial_points: recurringFormData.initial_points,
              // 06/2026 — Verzögerter Start (erste Rechnung am Datum X)
              delayed_start_date: recurringFormData.delayed_start_date,
              // 06/2026 — Konfigurierbarer Abbuchungstag
              billing_day: recurringFormData.billing_day,
              renewal_mode: recurringFormData.renewal_mode,
              low_balance_threshold: recurringFormData.low_balance_threshold,
              notes: fullNotes,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          toast.success(`✅ Wiederkehrendes Abo erfolgreich erstellt!`);
        }
      }
      
      resetForm();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Verkauf');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSelectedCustomer(null);
    setProductType('');
    setSelectedProduct(null);
    setPaymentMethod('');
    setNotes('');
    setDiscountType('fixed');
    setDiscountValue('');
    setOneTimeForm({ description: '', amount: '' });
    setOneTimeResult(null);
    setRecurringFormData({
      name: '',
      description: '',
      points_per_month: 100,
      price_per_month: 200,
      billing_cycle: 'monthly',
      payment_method: 'kreditkarte',
      auto_billing_method: 'invoice',
      allowed_service_ids: [],
      initial_price: null,
      initial_points: null,
      delayed_start_date: null,
      billing_day: null,
      renewal_mode: 'monthly_25',
      low_balance_threshold: 2,
    });
  };

  const getServiceName = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    return service ? service.name : 'Unbekannt';
  };

  // Get service points cost for calculation
  const getServicePointsCost = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    // Support both field names: points_per_session (newer) and points_cost (legacy)
    return service?.points_per_session || service?.points_cost || 1;
  };

  // Calculate sessions from credits and service points
  const calculateSessions = (sub) => {
    const credits = sub.credits_total || sub.credits_per_month || 0;
    
    // ALWAYS calculate: credits / points_per_service
    // Even for recurring subscriptions, credits are POINTS, not sessions
    if (sub.allowed_service_ids && sub.allowed_service_ids.length > 0) {
      const pointsCost = getServicePointsCost(sub.allowed_service_ids[0]);
      if (pointsCost > 0) {
        return Math.floor(credits / pointsCost);
      }
    }
    
    return credits; // Fallback if no service
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      'bar': '💰 Bar',
      'rechnung_combo': '📄 Rechnung / TWINT',
      'card': '💳 Gespeicherte Karte'
    };
    return labels[method] || method;
  };

  // 06/2026: Vorzugspreis-Badge — Team erkennt Rabatt beim Verkauf sofort.
  const renderDiscountBadge = (c) => {
    if (!c) return null;
    const pct = Number(c.discount_percent || 0);
    let label = null;
    if (pct > 0) label = `-${pct % 1 === 0 ? pct : pct.toFixed(1)}%`;
    else if (c.has_individual_prices) label = 'Vorzugspreis';
    if (!label) return null;
    return (
      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 mt-1 mr-1" data-testid="sales-customer-discount-badge">
        💚 {label}
      </Badge>
    );
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <ShoppingCart className="w-8 h-8 text-blue-600" />
        <h1 className="text-3xl font-bold">Verkaufen</h1>
      </div>

      <Card className="mb-6" data-testid="discount-code-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Badge className="bg-amber-100 text-amber-800">10% Rabatt</Badge>
            Re‑Engagement Rabattcodes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {selectedCustomer ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="text-sm text-gray-600">Ausgewählter Kunde</p>
                  <p className="font-semibold">{selectedCustomer.first_name} {selectedCustomer.last_name}</p>
                  <p className="text-xs text-gray-500">{selectedCustomer.email}</p>
                  {renderDiscountBadge(selectedCustomer)}
                </div>
                <Button
                  onClick={handleCreateDiscountCode}
                  disabled={discountCreating}
                  data-testid="create-discount-code-button"
                >
                  {discountCreating ? 'Erstellt...' : 'Rabattcode erstellen'}
                </Button>
              </div>

              <div className="border rounded-lg p-3 bg-gray-50">
                {discountLoading ? (
                  <p className="text-sm text-gray-500">Lädt Rabattcodes...</p>
                ) : discountCodes.length === 0 ? (
                  <p className="text-sm text-gray-500">Noch kein Rabattcode erstellt.</p>
                ) : (
                  <div className="space-y-2">
                    {discountCodes.map((code) => {
                      const isExpired = code.valid_until && new Date(code.valid_until) < new Date();
                      const status = code.is_used ? 'Eingelöst' : isExpired ? 'Abgelaufen' : 'Aktiv';
                      return (
                        <div key={code.id || code.code} className="flex items-center justify-between text-sm" data-testid={`discount-code-${code.code}`}>
                          <div>
                            <p className="font-semibold">{code.code} · {code.discount_percent}%</p>
                            <p className="text-xs text-gray-500">Gültig bis {code.valid_until ? new Date(code.valid_until).toLocaleDateString('de-CH') : '—'}</p>
                          </div>
                          <Badge variant="outline">{status}</Badge>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-500">
                Hinweis: Der Rabatt ist einmalig und wird im Kundenportal automatisch angezeigt (Shop 6 Checkout).
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Bitte zuerst einen Kunden auswählen (Schritt 1), um Rabattcodes zu erstellen.</p>
          )}
        </CardContent>
      </Card>

      {/* Progress Steps */}
      <div className="mb-8 flex items-center justify-center gap-4">
        {[1, 2, 3, 4].map((s) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
              step >= s ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {step > s ? <Check className="w-5 h-5" /> : s}
            </div>
            {s < 4 && <div className={`w-16 h-1 ${step > s ? 'bg-blue-600' : 'bg-gray-200'}`} />}
          </div>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {step === 1 && '1. Kunde auswählen'}
            {step === 2 && '2. Produkt-Typ wählen'}
            {step === 3 && '3. Produkt auswählen'}
            {step === 4 && '4. Bezahlung & Bestätigung'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Step 1: Kunde */}
          {step === 1 && (
            <div>
              <Label>Kunde suchen</Label>
              <Input
                type="text"
                placeholder="Name oder Email eingeben..."
                value={customerSearch}
                onChange={(e) => handleCustomerSearch(e.target.value)}
                className="mb-4"
              />
              
              <div className="max-h-96 overflow-y-auto border rounded-lg">
                {filteredCustomers.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    Keine Kunden gefunden
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredCustomers.map(customer => (
                      <button
                        key={customer.id}
                        onClick={() => handleCustomerSelect(customer)}
                        className="w-full p-4 text-left hover:bg-blue-50 transition-colors"
                      >
                        <p className="font-bold">{customer.first_name} {customer.last_name}</p>
                        <p className="text-sm text-gray-600">{customer.email}</p>
                        <div className="flex flex-wrap items-center">
                        {renderDiscountBadge(customer)}
                        {customer.billing_mode && (
                          <Badge variant="outline" className="mt-1">
                            {getPaymentMethodLabel(customer.billing_mode === 'kreditkarte' || customer.billing_mode === 'card' ? 'card' : customer.billing_mode === 'bar' ? 'bar' : 'rechnung_combo')}
                          </Badge>
                        )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Produkt-Typ */}
          {step === 2 && selectedCustomer && (
            <div>
              <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600">Ausgewählter Kunde:</p>
                <p className="font-bold">{selectedCustomer.first_name} {selectedCustomer.last_name}</p>
                {renderDiscountBadge(selectedCustomer)}
                <p className="text-sm text-gray-600 mt-1">Standard-Zahlung: {getPaymentMethodLabel(selectedCustomer.billing_mode || 'rechnung')}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <button
                  onClick={() => handleProductTypeSelect('points')}
                  className="p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                  data-testid="sales-type-points-btn"
                >
                  <Package className="w-8 h-8 text-blue-600 mb-2" />
                  <h3 className="font-bold text-lg mb-1">Punkt-Paket</h3>
                  <p className="text-sm text-gray-600">Normale Punkte für alle Services</p>
                </button>

                <button
                  onClick={() => handleProductTypeSelect('service_abo')}
                  className="p-6 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all text-left"
                  data-testid="sales-type-service-abo-btn"
                >
                  <Package className="w-8 h-8 text-green-600 mb-2" />
                  <h3 className="font-bold text-lg mb-1">Service-Abo (Slider)</h3>
                  <p className="text-sm text-gray-600">Menge wählen — Staffelrabatt automatisch</p>
                </button>

                <button
                  onClick={() => handleProductTypeSelect('subscription')}
                  className="p-6 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all text-left"
                  data-testid="sales-type-subscription-btn"
                >
                  <CreditCard className="w-8 h-8 text-green-600 mb-2" />
                  <h3 className="font-bold text-lg mb-1">10er-Abo</h3>
                  <p className="text-sm text-gray-600">Feste Abo-Vorlagen</p>
                </button>

                <button
                  onClick={() => handleProductTypeSelect('recurring')}
                  className="p-6 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all text-left"
                  data-testid="sales-type-recurring-btn"
                >
                  <RefreshCw className="w-8 h-8 text-purple-600 mb-2" />
                  <h3 className="font-bold text-lg mb-1">Wiederkehrendes Abo</h3>
                  <p className="text-sm text-gray-600">Monatlich mit Auto-Reload</p>
                </button>
              </div>

              <Button variant="outline" onClick={() => setStep(1)} className="mt-4">
                Zurück
              </Button>
            </div>
          )}

          {/* Step 3: Produkt */}
          {step === 3 && productType && (
            <div>
              <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600">Kunde: {selectedCustomer.first_name} {selectedCustomer.last_name}</p>
                <Badge className="mt-1">
                  {productType === 'points' ? 'Punkt-Paket' : productType === 'service_abo' ? 'Service-Abo' : productType === 'subscription' ? '10er-Abo' : 'Wiederkehrendes Abo'}
                </Badge>
              </div>

              {productType === 'service_abo' && (
                <AdminServiceAboCalculator
                  API={API}
                  customerId={selectedCustomer?.id}
                  onSelect={(data) => { setSelectedProduct(data); setStep(4); }}
                />
              )}

              {productType === 'points' && (
                <div className="space-y-3">
                  {pointPackages.map(pkg => (
                    <button
                      key={pkg.id}
                      onClick={() => handleProductSelect(pkg)}
                      className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="font-bold">{pkg.name}</h3>
                          <p className="text-sm text-gray-600">{pkg.description}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg">{pkg.points_amount} Punkte</p>
                          <p className="text-sm text-gray-600">CHF {pkg.price}</p>
                        </div>
                      </div>
                    </button>
                  ))}

                  {pointPackages.length > 0 && (
                    <div className="flex items-center gap-3 py-1">
                      <div className="flex-1 h-px bg-gray-200" />
                      <span className="text-xs text-gray-400">oder</span>
                      <div className="flex-1 h-px bg-gray-200" />
                    </div>
                  )}

                  {/* Freie Punkteanzahl mit Staffelrabatt + Bonus/Rabatt (wie Kundenshop) */}
                  <AdminPointsCalculator API={API} customerId={selectedCustomer?.id} onSelect={handleProductSelect} />
                </div>
              )}

              {productType === 'subscription' && (
                <div className="space-y-3">
                  {/* Show only one-time packages (billing_cycle = 'once') */}
                  {subscriptionTemplates.filter(s => s.billing_cycle === 'once').map(sub => {
                    const sessions = calculateSessions(sub);
                    const serviceName = sub.allowed_service_ids?.length > 0 
                      ? getServiceName(sub.allowed_service_ids[0]) 
                      : 'Einheiten';
                    return (
                      <button
                        key={sub.id}
                        onClick={() => handleProductSelect(sub)}
                        className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all text-left"
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <h3 className="font-bold">{sub.name}</h3>
                            {sub.allowed_service_ids && sub.allowed_service_ids.length > 0 && (
                              <p className="text-sm text-green-600">
                                🔒 Für: {sub.allowed_service_ids.map(id => getServiceName(id)).join(', ')}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-lg">{sessions}x {serviceName}</p>
                            <p className="text-sm text-gray-600">CHF {sub.price}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                  {subscriptionTemplates.filter(s => s.billing_cycle === 'once').length === 0 && (
                    <p className="text-gray-500 text-center py-4">Keine 10er-Abos verfügbar</p>
                  )}
                </div>
              )}

              {productType === 'recurring' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 mb-4">
                    Wähle ein bestehendes Abo-Template aus oder erstelle ein individuelles Abo:
                  </p>
                  
                  {/* Show recurring templates (monthly/yearly) */}
                  <div className="space-y-3">
                    {subscriptionTemplates.filter(s => s.billing_cycle === 'monthly' || s.billing_cycle === 'yearly' || s.billing_cycle === 'weekly').map(sub => {
                      const sessions = calculateSessions(sub);
                      const serviceName = sub.allowed_service_ids?.length > 0 
                        ? getServiceName(sub.allowed_service_ids[0]) 
                        : 'Einheiten';
                      const billingText = sub.billing_cycle === 'monthly' ? 'Monat' : sub.billing_cycle === 'yearly' ? 'Jahr' : 'Woche';
                      return (
                        <button
                          key={sub.id}
                          onClick={() => {
                            setSelectedProduct(sub);
                            setPaymentMethod('card');
                            setStep(4);
                          }}
                          className="w-full p-4 border-2 border-purple-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all text-left"
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <div className="flex items-center gap-2">
                                <RefreshCw className="w-4 h-4 text-purple-600" />
                                <h3 className="font-bold">{sub.name}</h3>
                                <Badge className="bg-purple-100 text-purple-700 text-xs">
                                  {sub.billing_cycle === 'monthly' ? 'Monatlich' : sub.billing_cycle === 'yearly' ? 'Jährlich' : 'Wöchentlich'}
                                </Badge>
                              </div>
                              {sub.allowed_service_ids && sub.allowed_service_ids.length > 0 && (
                                <p className="text-sm text-purple-600 mt-1">
                                  🔒 Für: {sub.allowed_service_ids.map(id => getServiceName(id)).join(', ')}
                                </p>
                              )}
                              {sub.description && (
                                <p className="text-sm text-gray-500 mt-1">{sub.description}</p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-lg">{sessions}x {serviceName}</p>
                              <p className="text-sm text-gray-600">CHF {sub.price} / {billingText}</p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {subscriptionTemplates.filter(s => s.billing_cycle === 'monthly' || s.billing_cycle === 'yearly' || s.billing_cycle === 'weekly').length === 0 && (
                    <p className="text-gray-500 text-center py-4">Keine wiederkehrenden Abo-Vorlagen verfügbar</p>
                  )}

                  {/* Option to create custom recurring subscription */}
                  <div className="border-t pt-4 mt-4">
                    <button
                      onClick={() => {
                        setSelectedProduct(null);
                        setPaymentMethod('card');
                        setStep(4);
                      }}
                      className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-400 hover:bg-purple-50/50 transition-all text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                          <span className="text-purple-600 font-bold text-xl">+</span>
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-700">Individuelles Abo erstellen</h3>
                          <p className="text-sm text-gray-500">Eigene Konditionen festlegen</p>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              <Button variant="outline" onClick={() => setStep(2)} className="mt-4">
                Zurück
              </Button>
            </div>
          )}

          {/* Step 4: Bezahlung */}
          {step === 4 && (
            <div>
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">Zusammenfassung:</p>
                <p className="font-bold">Kunde: {selectedCustomer.first_name} {selectedCustomer.last_name}</p>
                {selectedProduct && <p className="font-bold">Produkt: {selectedProduct.name}</p>}
                {productType === 'recurring' && !selectedProduct && <p className="font-bold">Produkt: Individuelles Abo</p>}
                {productType === 'one_time' && <p className="font-bold">Produkt: Einmalige Rechnung</p>}
                {selectedProduct && productType === 'recurring' && (
                  <Badge className="bg-purple-100 text-purple-700 mt-1">
                    {selectedProduct.billing_cycle === 'monthly' ? 'Monatlich' : selectedProduct.billing_cycle === 'yearly' ? 'Jährlich' : 'Wöchentlich'}
                  </Badge>
                )}
                {(selectedProduct || productType === 'recurring' || productType === 'one_time') && (
                  <p className="font-bold text-lg text-blue-600 mt-2" data-testid="sales-summary-total">
                    Total: CHF {(
                      Number(
                        productType === 'one_time'
                          ? oneTimeForm.amount
                          : productType === 'recurring' && selectedProduct
                            ? selectedProduct.price
                            : productType === 'recurring'
                              ? recurringFormData.price_per_month
                              : selectedProduct?.price
                      ) || 0
                    ).toFixed(2)}
                  </p>
                )}
              </div>

              {/* 06/2026: Einmalige Rechnung — Formular + Result */}
              {productType === 'one_time' && (
                <div className="space-y-4 mb-4">
                  {!oneTimeResult && (
                    <>
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800">
                        📄 Erzeugt eine <strong>einmalige Rechnung</strong> mit fixem Betrag — inklusive Swiss QR-Code und Payrex-Bezahllink. Kein Abo, keine wiederkehrende Belastung.
                      </div>
                      <div>
                        <Label htmlFor="ot-description">Beschreibung *</Label>
                        <Input
                          id="ot-description"
                          value={oneTimeForm.description}
                          onChange={(e) => setOneTimeForm({ ...oneTimeForm, description: e.target.value })}
                          placeholder="z.B. Beratungsstunde 60 Min., Personal Training, Material…"
                          data-testid="one-time-description-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="ot-amount">Betrag (CHF) *</Label>
                        <Input
                          id="ot-amount"
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={oneTimeForm.amount}
                          onChange={(e) => setOneTimeForm({ ...oneTimeForm, amount: e.target.value })}
                          placeholder="z.B. 150.00"
                          data-testid="one-time-amount-input"
                        />
                      </div>
                    </>
                  )}

                  {oneTimeResult && (
                    <div
                      className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-2"
                      data-testid="one-time-result-card"
                    >
                      <p className="font-bold text-emerald-800">✅ Rechnung erstellt</p>
                      <p className="text-sm">
                        <strong>Nummer:</strong> {oneTimeResult.invoice_number}
                      </p>
                      <p className="text-sm">
                        <strong>Betrag:</strong> CHF {oneTimeResult.total_amount}
                      </p>
                      <p className="text-sm break-all">
                        <strong>QR-Referenz:</strong> {oneTimeResult.qr_reference || '—'}
                      </p>
                      {oneTimeResult.payrexx_payment_link && (
                        <div className="text-sm">
                          <strong>Payrex-Link:</strong>{' '}
                          <a
                            href={oneTimeResult.payrexx_payment_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 underline break-all"
                            data-testid="one-time-payrex-link"
                          >
                            {oneTimeResult.payrexx_payment_link}
                          </a>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2 mt-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(oneTimeResult.payrexx_payment_link || '');
                            toast.success('Link kopiert');
                          }}
                          disabled={!oneTimeResult.payrexx_payment_link}
                        >
                          🔗 Link kopieren
                        </Button>
                        <Button
                          size="sm"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              const { data } = await axios.post(
                                `${API}/invoices/${oneTimeResult.invoice_id}/dispatch`,
                                { channels: ['email'] },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              const sent = data?.channels?.email?.sent;
                              if (sent) toast.success(`📧 Email an ${data.channels.email.to} gesendet`);
                              else toast.warning(`Email nicht gesendet: ${data?.channels?.email?.reason || 'unbekannt'}`);
                            } catch (e) {
                              toast.error('Email-Versand fehlgeschlagen');
                            }
                          }}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid="one-time-send-email-btn"
                        >
                          📧 Per Email senden
                        </Button>
                        <Button
                          size="sm"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              const { data } = await axios.post(
                                `${API}/invoices/${oneTimeResult.invoice_id}/dispatch`,
                                { channels: ['whatsapp'] },
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              const sent = data?.channels?.whatsapp?.sent;
                              if (sent) toast.success(`💬 WhatsApp an ${data.channels.whatsapp.to} gesendet`);
                              else toast.warning(`WhatsApp nicht gesendet: ${data?.channels?.whatsapp?.reason || 'unbekannt'}`);
                            } catch (e) {
                              toast.error('WhatsApp-Versand fehlgeschlagen');
                            }
                          }}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid="one-time-send-whatsapp-btn"
                        >
                          💬 Per WhatsApp senden
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={resetForm}
                        >
                          Neuer Verkauf
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-4">
                {/* Recurring Subscription from Template - Simplified view */}
                {productType === 'recurring' && selectedProduct && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <RefreshCw className="w-5 h-5 text-purple-600 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-purple-800">{selectedProduct.name}</h4>
                        <p className="text-sm text-purple-700">
                          {calculateSessions(selectedProduct)} Trainings / {selectedProduct.billing_cycle === 'monthly' ? 'Monat' : selectedProduct.billing_cycle === 'yearly' ? 'Jahr' : 'Woche'}
                        </p>
                        {selectedProduct.allowed_service_ids && selectedProduct.allowed_service_ids.length > 0 && (
                          <p className="text-sm text-purple-600 mt-1">
                            Für: {selectedProduct.allowed_service_ids.map(id => getServiceName(id)).join(', ')}
                          </p>
                        )}
                        <p className="font-bold text-purple-800 mt-2">CHF {selectedProduct.price}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Recurring Subscription Configuration - Only for custom subscriptions */}
                {productType === 'recurring' && !selectedProduct && (
                  <>
                    {/* Formular-Aufbau analog Kundenprofil: Name, Beschreibung, Preise, Auto-Reload usw. */}
                    <div>
                      <Label htmlFor="rec-name">Abo-Name *</Label>
                      <Input
                        id="rec-name"
                        value={recurringFormData.name}
                        onChange={(e) => setRecurringFormData({ ...recurringFormData, name: e.target.value })}
                        placeholder="z.B. Monatsabo 100 Punkte"
                      />
                    </div>

                    <div>
                      <Label htmlFor="rec-description">Beschreibung</Label>
                      <Input
                        id="rec-description"
                        value={recurringFormData.description}
                        onChange={(e) => setRecurringFormData({ ...recurringFormData, description: e.target.value })}
                        placeholder="Optional: Zusätzliche Informationen"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="rec-points">Punkte pro Monat *</Label>
                        <Input
                          id="rec-points"
                          type="number"
                          min="1"
                          value={recurringFormData.points_per_month === 0 ? '' : recurringFormData.points_per_month}
                          onChange={(e) => setRecurringFormData({
                            ...recurringFormData,
                            points_per_month: e.target.value === '' ? 0 : parseInt(e.target.value) || 0,
                          })}
                        />
                      </div>

                      <div>
                        <Label htmlFor="rec-price">Preis pro Monat (CHF) *</Label>
                        <Input
                          id="rec-price"
                          type="number"
                          min="0"
                          step="0.01"
                          value={recurringFormData.price_per_month === 0 ? '' : recurringFormData.price_per_month}
                          onChange={(e) => setRecurringFormData({
                            ...recurringFormData,
                            price_per_month: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0,
                          })}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="rec-initial-price" className="flex items-center gap-1">
                          Anderer Betrag für 1. Rechnung (CHF)
                          <span
                            className="text-xs text-gray-500 cursor-default"
                            title="Optional. Nur für die ALLERERSTE Rechnung wird dieser Betrag verwendet — ab der zweiten Monatsrechnung gilt wieder der normale Monatspreis. Leer lassen = erste Rechnung gleich Monatspreis."
                          >
                            ⓘ
                          </span>
                        </Label>
                        <Input
                          id="rec-initial-price"
                          type="number"
                          min="0"
                          step="0.01"
                          value={recurringFormData.initial_price ?? ''}
                          onChange={(e) =>
                            setRecurringFormData({
                              ...recurringFormData,
                              initial_price:
                                e.target.value === '' ? null : parseFloat(e.target.value) || 0,
                            })
                          }
                          placeholder="Leer = wie Monatsbetrag"
                          data-testid="rec-initial-price-input"
                        />
                        <p className="text-[11px] text-gray-500 mt-1">
                          z.B. <strong>99</strong> als Einstiegspreis erste Rechnung,
                          danach jeden Monat normal.
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="rec-initial-points" className="flex items-center gap-1">
                          Andere Punkte für 1. Monat
                          <span
                            className="text-xs text-gray-500 cursor-default"
                            title="Optional. Wenn die ersten Punkte abweichen sollen (z.B. anteilig für einen Halbmonats-Start). Leer lassen = gleiche Punkte wie monatlich."
                          >
                            ⓘ
                          </span>
                        </Label>
                        <Input
                          id="rec-initial-points"
                          type="number"
                          min="0"
                          value={recurringFormData.initial_points ?? ''}
                          onChange={(e) =>
                            setRecurringFormData({
                              ...recurringFormData,
                              initial_points:
                                e.target.value === '' ? null : parseInt(e.target.value) || 0,
                            })
                          }
                          placeholder="Leer = wie Monatspunkte"
                          data-testid="rec-initial-points-input"
                        />
                        <p className="text-[11px] text-gray-500 mt-1">
                          Punkte werden nach 1. Zahlung gutgeschrieben.
                        </p>
                      </div>
                    </div>

                    {/* 06/2026 — „Erste Rechnung am Datum X" */}
                    <div>
                      <Label htmlFor="rec-delayed-start" className="flex items-center gap-1">
                        Erste Rechnung geht raus am
                        <span
                          className="text-xs text-gray-500 cursor-default"
                          title="Optional. Wenn gesetzt, wird das Abo zwar jetzt angelegt — die erste Rechnung (und die Punkte) gehen aber erst zu diesem Datum raus. Beispiel: Verkauf heute 16.06., Datum 01.07. → erste Rechnung kommt am 01.07., dann normal jeden 25."
                        >
                          ⓘ
                        </span>
                      </Label>
                      <Input
                        id="rec-delayed-start"
                        type="date"
                        value={recurringFormData.delayed_start_date || ''}
                        onChange={(e) =>
                          setRecurringFormData({
                            ...recurringFormData,
                            delayed_start_date: e.target.value || null,
                          })
                        }
                        data-testid="rec-delayed-start-input"
                      />
                      <p className="text-[11px] text-gray-500 mt-1">
                        Leer lassen = erste Rechnung sofort beim Verkauf. Sonst startet das Abo
                        ab diesem Datum (z.B. Probemonat-Ende).
                      </p>
                    </div>

                    {/* 06/2026 — Abbuchungstag (1-28) konfigurierbar */}
                    <div>
                      <Label htmlFor="rec-billing-day" className="flex items-center gap-1">
                        Abbuchungstag (1–28)
                        <span
                          className="text-xs text-gray-500 cursor-default"
                          title="Optional. An welchem Tag des Monats die wiederkehrende Rechnung ausgestellt werden soll. Leer = globaler Standard aus Settings → Automation (Default 25.). Beispiel: 1 = jeden 1. des Monats. Praktisch zum Testen ohne 25. abzuwarten."
                        >
                          ⓘ
                        </span>
                      </Label>
                      <Input
                        id="rec-billing-day"
                        type="number"
                        min="1"
                        max="28"
                        step="1"
                        value={recurringFormData.billing_day ?? ''}
                        onChange={(e) =>
                          setRecurringFormData({
                            ...recurringFormData,
                            billing_day:
                              e.target.value === '' ? null : Math.min(28, Math.max(1, parseInt(e.target.value) || 25)),
                          })
                        }
                        placeholder="Leer = Standard aus Settings"
                        data-testid="rec-billing-day-input"
                      />
                      <p className="text-[11px] text-gray-500 mt-1">
                        Tipp: <strong>1</strong> für „erster jedes Monats" — zum Testen ideal.
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="rec-billing-cycle">Abrechnungszyklus</Label>
                      <Select
                        value={recurringFormData.billing_cycle}
                        onValueChange={(value) =>
                          setRecurringFormData({ ...recurringFormData, billing_cycle: value })
                        }
                      >
                        <SelectTrigger id="rec-billing-cycle">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">Monatlich</SelectItem>
                          <SelectItem value="quarterly">Vierteljährlich</SelectItem>
                          <SelectItem value="yearly">Jährlich</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="border-t pt-4">
                      <Label htmlFor="rec-renewal-mode">Verlängerungsmodus</Label>
                      <Select
                        value={recurringFormData.renewal_mode}
                        onValueChange={(value) =>
                          setRecurringFormData({ ...recurringFormData, renewal_mode: value })
                        }
                      >
                        <SelectTrigger id="rec-renewal-mode">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly_25">Monatlich am Fixdatum</SelectItem>
                          <SelectItem value="on_depletion">Bei Punkteverbrauch (Auto-Reload)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-600 mt-1">
                        {recurringFormData.renewal_mode === 'monthly_25'
                          ? `📅 Automatische Abbuchung jeden Monat am ${recurringFormData.billing_day || 25}.`
                          : '🔄 Automatische Aufladung wenn Punkte aufgebraucht sind'}
                      </p>
                    </div>

                    {/* Service-Einschränkungen (Optional) */}
                    <div>
                      <Label>Service-Einschränkungen (Optional)</Label>
                      <p className="text-sm text-gray-600 mb-2">
                        Wählen Sie Services aus, für die diese Punkte verwendet werden dürfen. Leer = Alle Services erlaubt.
                      </p>
                      <div className="max-h-48 overflow-y-auto border rounded p-3 space-y-2">
                        {services.map((service) => {
                          const isChecked = recurringFormData.allowed_service_ids.includes(service.id);
                          return (
                            <label key={service.id} className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setRecurringFormData({
                                      ...recurringFormData,
                                      allowed_service_ids: [...recurringFormData.allowed_service_ids, service.id],
                                    });
                                  } else {
                                    setRecurringFormData({
                                      ...recurringFormData,
                                      allowed_service_ids: recurringFormData.allowed_service_ids.filter(
                                        (id) => id !== service.id
                                      ),
                                    });
                                  }
                                }}
                                className="rounded"
                              />
                              <span className="text-sm">
                                {service.name} (CHF {service.price?.toFixed(2)})
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {recurringFormData.allowed_service_ids.length === 0
                          ? 'Alle Services erlaubt (keine Einschränkung)'
                          : `${recurringFormData.allowed_service_ids.length} Service(s) ausgewählt`}
                      </p>
                    </div>
                  </>
                )}

                <div>
                  <Label>Zahlungsmethode</Label>
                  {productType === 'recurring' ? (
                    <div className="space-y-2" data-testid="recurring-billing-method-group">
                      <button
                        type="button"
                        onClick={() => setRecurringFormData({ ...recurringFormData, auto_billing_method: 'invoice' })}
                        data-testid="recurring-billing-invoice-btn"
                        className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                          recurringFormData.auto_billing_method === 'invoice'
                            ? 'bg-blue-50 border-blue-400 ring-2 ring-blue-200'
                            : 'bg-white border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <span className="text-2xl mt-0.5">📄</span>
                          <div className="flex-1">
                            <p className="font-bold text-gray-900">Rechnung (QR / TWINT)</p>
                            <p className="text-xs text-gray-600 mt-1">
                              Kunde erhält jeden Monat (am {recurringFormData.billing_day || 25}.) eine QR-Rechnung per E-Mail/WhatsApp.
                              Bezahlung manuell — empfohlen für Neukunden ohne hinterlegte Karte.
                            </p>
                          </div>
                          {recurringFormData.auto_billing_method === 'invoice' && (
                            <span className="text-blue-600 font-bold">✓</span>
                          )}
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => setRecurringFormData({ ...recurringFormData, auto_billing_method: 'card' })}
                        data-testid="recurring-billing-card-btn"
                        className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                          recurringFormData.auto_billing_method === 'card'
                            ? 'bg-purple-50 border-purple-400 ring-2 ring-purple-200'
                            : 'bg-white border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <span className="text-2xl mt-0.5">💳</span>
                          <div className="flex-1">
                            <p className="font-bold text-gray-900">Kreditkarte (Auto-Charge)</p>
                            <p className="text-xs text-gray-600 mt-1">
                              Automatische Belastung der gespeicherten Karte via Payrexx am {recurringFormData.billing_day || 25}.
                              Nach 3 Fehlversuchen automatischer Fallback auf Rechnung.
                            </p>
                            <p className="text-xs text-orange-600 mt-1">
                              ⚠️ Voraussetzung: Kunde hat eine Karte im Portal hinterlegt.
                            </p>
                          </div>
                          {recurringFormData.auto_billing_method === 'card' && (
                            <span className="text-purple-600 font-bold">✓</span>
                          )}
                        </div>
                      </button>
                    </div>
                  ) : (
                    <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bar">{getPaymentMethodLabel('bar')}</SelectItem>
                        <SelectItem value="rechnung_combo" disabled={zeroCheckout}>{getPaymentMethodLabel('rechnung_combo')}</SelectItem>
                        <SelectItem value="card" disabled={zeroCheckout}>{getPaymentMethodLabel('card')}</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  {zeroCheckout && (
                    <p className="text-xs text-amber-600 mt-1" data-testid="sales-zero-amount-hint">
                      💵 CHF 0.00 → nur Barzahlung möglich. Begründung in die Notizen schreiben — der Admin wird per E-Mail informiert.
                    </p>
                  )}
                </div>

                <div>
                  <Label>{noteRequired ? '📝 Notiz (erforderlich — Begründung für Rabatt/CHF 0)' : 'Notizen (optional)'}</Label>
                  <Input
                    placeholder={noteRequired ? 'z.B. Gutschein Eröffnung, Reklamation, Mitarbeiterkonditionen…' : 'z.B. Rabatt, Sonderpreis...'}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className={noteRequired && !notes.trim() ? 'border-amber-400' : ''}
                    data-testid="sales-notes-input"
                  />
                </div>

                {/* NEU: Rabatt-Sektion (nicht bei Service-Abo — dort steckt der
                    Staffelrabatt bereits im Slider-Preis) */}
                {productType !== 'service_abo' && (
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <Label className="text-yellow-800 font-semibold mb-2 block">🏷️ Rabatt (optional)</Label>
                  <div className="flex gap-3 items-end">
                    <div className="flex-1">
                      <Select value={discountType} onValueChange={setDiscountType}>
                        <SelectTrigger className="bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fixed">CHF (Fixbetrag)</SelectItem>
                          <SelectItem value="percent">% (Prozent)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex-1">
                      <Input
                        type="number"
                        min="0"
                        max={discountType === 'percent' ? 100 : undefined}
                        step={discountType === 'percent' ? '1' : '0.01'}
                        placeholder={discountType === 'percent' ? 'z.B. 10 (bis 100)' : 'z.B. 25.00'}
                        value={discountValue}
                        onChange={(e) => setDiscountValue(e.target.value)}
                        className="bg-white"
                      />
                    </div>
                  </div>
                  {discountValue && parseFloat(discountValue) > 0 && (
                    <div className="mt-3 p-2 bg-white rounded border border-yellow-300">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Originalpreis:</span>
                        <span>CHF {calculateDiscount().originalPrice?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-red-600">
                        <span>Rabatt ({discountType === 'percent' ? `${discountValue}%` : `CHF ${discountValue}`}):</span>
                        <span>- CHF {calculateDiscount().discount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg mt-1 pt-1 border-t border-yellow-200">
                        <span className="text-green-700">Endpreis:</span>
                        <span className="text-green-700">CHF {calculateDiscount().finalPrice.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </div>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => productType === 'one_time' ? setStep(2) : productType === 'recurring' ? setStep(2) : setStep(3)}>
                  Zurück
                </Button>
                {!(productType === 'one_time' && oneTimeResult) && (
                  <Button 
                    onClick={handlePurchase} 
                    disabled={
                      loading ||
                      (productType === 'recurring' && (!recurringFormData.name || recurringFormData.points_per_month <= 0 || recurringFormData.price_per_month <= 0)) ||
                      (productType === 'one_time' && (!oneTimeForm.description || !oneTimeForm.amount || parseFloat(oneTimeForm.amount) <= 0))
                    } 
                    className="flex-1"
                    data-testid="sales-submit-btn"
                  >
                    {loading ? 'Wird verarbeitet...' : productType === 'one_time' ? '📄 Rechnung erstellen' : '✅ Verkauf abschliessen'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SalesPage;
